class Theme {
  constructor (element, isEditor) {
    this.element = element
    this.isEditor = isEditor
    this.pageTheme = 'default'
    this.sections = this.element.querySelectorAll('.js-theme')
    this.currentY = this.getScroll()
    this.onFirstScroll = this._onFirstScroll.bind(this)

    this.init()
  }

  init () {
    this.observer = this.registerObserver()
    this.setSections()

    // Set initial page theme.
    this.setTheme(this.getFirstElement())

    this.setACFListeners()

    if (this.isEditor) {
      this.listenForBlockRemoval()
    }

    this.element.addEventListener('scroll', this.onFirstScroll)
  }

  /**
   * @desc Creates an observer to set theme based on section.
   */
  registerObserver() {
    const options = {
      root: this.isEditor ? this.element : null,
      rootMargin: '0px 0px -50%',
      threshold: 0
    }

    return new IntersectionObserver(this.sectionInView.bind(this), options)
  }

  listenForBlockRemoval() {
    const targetNode = document.querySelector('.block-editor-block-list__layout')
    const config = { childList: true }
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
          if (mutation.removedNodes[0].dataset.type && mutation.removedNodes[0].dataset.type === 'acf/theme') {
            this.setSections()
          }
        }
      }
    }
    const observer = new MutationObserver(callback)
    observer.observe(targetNode, config)
  }

  /**
   * @desc Callback for intersectionObserver that says which theme is in the viewport.
   * @param {HTMLElement} sections - List of sections from the observer
   */
  sectionInView(sections) {
    sections.forEach(section => {
      const direction = this.getScroll() >= this.currentY ? 'down' : 'up'
      this.currentY = this.getScroll()

      if (this.currentY <= 0) {
        this.setTheme(this.getFirstElement())
      } else if (direction === 'down' && section.isIntersecting) {
        this.setTheme(section.target)
      } else if (direction === 'up' && !section.isIntersecting) {
        const index = Math.max(parseInt(section.target.dataset.themeIndex) - 1, 0)
        if (index === 0) {
          this.setTheme(this.getFirstElement())
        } else {
          this.setTheme(this.sections[index - (this.isEditor ? 1 : 0)])
        }
      }
    })
  }

  /**
   * @desc If the section is visible, set its theme appropriately.
   * @param {HTMLElement} section - The section that is in the viewport
   */
  setTheme(section) {
    const theme = section.dataset.theme

    if (theme) {
      this.element.dataset.theme = theme
    }
  }

  setSections() {
    this.sections = Array.from(this.element.querySelectorAll('.js-theme'))
    this.sections.forEach((section, index) => {
      section.dataset.themeIndex = index + (this.isEditor ? 1 : 0)
      this.observer.observe(section)
    })
  }

  _onFirstScroll() {
    let currentSection = this.getFirstElement()
    this.sections.forEach((section, index) => {
      if (section.getBoundingClientRect().top <= window.innerHeight / 2) {
        currentSection = section
      }
    })
    this.setTheme(currentSection)

    this.element.removeEventListener('scroll', this.onFirstScroll)
  }

  getScroll() {
    return this.element.scrollTop || window.scrollY
  }

  getPageTheme() {
    if ('acf' in window && acf.getFields({ name: 'theme', is: ':visible' }).length > 0) {
      const theme = acf.getFields({ name: 'theme', is: ':visible' })[0].val()
      if (theme === 'default') {
        return 'default'
      } else if (theme === 'black') {
        return 'black'
      }
      const mode = acf.getFields({ name: 'theme_mode', is: ':visible' })[0].val()
      const color = acf.getFields({ name: 'theme_color', is: ':visible' })[0].val()
      this.pageTheme = `${theme}-${mode}-${color}`
      return this.pageTheme
    }

    return this.pageTheme
  }

  setACFListeners() {
    if ('acf' in window) {
      acf.addAction('render_block_preview/type=theme', () => {
        this.setSections()
      })
      acf.getFields({ name: 'theme' })[0].on('change', () => {
        this.sectionInView(this.sections)
      })
      acf.getFields({ name: 'theme_mode' }).forEach(mode => {
        mode.on('change', () => {
          this.sectionInView(this.sections)
        })
      })
      acf.getFields({ name: 'theme_color' }).forEach(color => {
        color.on('change', () => {
          this.sectionInView(this.sections)
        })
      })
    }
  }

  getFirstElement() {
    if (this.isEditor) {
      return {
        dataset: {
          theme: this.getPageTheme(),
          themeIndex: 0
        }
      }
    } else {
      return this.sections[0]
    }
  }
}

export default {
  selector: 'body',
  Source: Theme
}
